import React, { useState, useEffect } from 'react';
import { useSession } from '../contexts/SessionContext';

const AUDITPLUS_DISMISS_KEY = 'auditplus_launch_banner_dismissed';
const AUDITPLUS_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const AUDITPLUS_SIGNUP_URL = 'https://www.auditplus.ai/register/partner/HYmBSii3SYODxx4Tyem40g';
const AUDITPLUS_DEMO_URL = 'https://www.youtube.com/watch?v=0P6IHgRVSVE';

const isBannerDismissed = (): boolean => {
  const raw = localStorage.getItem(AUDITPLUS_DISMISS_KEY);
  // Older builds stored "true" forever. That no longer hides the banner.
  if (!raw || raw === 'true') return false;
  const dismissedAt = Number(raw);
  if (!Number.isFinite(dismissedAt)) return false;
  return Date.now() - dismissedAt < AUDITPLUS_DISMISS_MS;
};

export const CreateOrgInfoBanner: React.FC = () => {
  const { activeOrg } = useSession();
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const isMY = activeOrg?.countryIso === 'MY';

  useEffect(() => {
    if (!isMY) return;
    if (!isBannerDismissed()) {
      setShowBanner(true);
    }
  }, [isMY]);

  const handleDismissBanner = () => {
    localStorage.setItem(AUDITPLUS_DISMISS_KEY, String(Date.now()));
    setShowBanner(false);
  };

  if (!showBanner && !showModal) {
    return null;
  }

  return (
    <div style={{ marginTop: 0 }}>
      {showBanner && (
      <div className="w-full text-white py-3 px-4 shadow-md mb-4 rounded-xl" style={{ backgroundColor: '#5B4FE0' }}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-snug">
                AI drafts your audited or unaudited financial statements from the trial balance.
              </p>
              <p className="text-sm text-white/80 leading-snug">
                You review and sign off, then export to RapidTagger.
                <button
                  type="button"
                  onClick={() => setShowModal(true)}
                  className="ml-2 underline hover:no-underline font-medium whitespace-nowrap"
                >
                  Learn more
                </button>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={AUDITPLUS_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#3d3499] hover:bg-indigo-50 px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
            >
              Start free trial
            </a>
            <button
              type="button"
              onClick={handleDismissBanner}
              className="text-white/80 hover:text-white p-1"
              aria-label="Dismiss Audit+ banner"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl flex flex-col max-h-[90vh]">
            <div className="flex items-start justify-between px-6 py-5 border-b">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Introducing Audit+ — financial statement preparation made easier</h3>
                  <p className="text-xs text-gray-500 mt-0.5">From trial balance to XBRL — one connected workflow</p>
                </div>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 ml-3" aria-label="Close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4 text-sm text-gray-600">
              <p>
                We're pleased to introduce <strong>Audit+</strong>, our new software partner that helps audit firms streamline the preparation of audited and unaudited financial statements.
              </p>
              <p>
                From importing trial balances and managing statutory information to account mapping, lead schedules, and drafting financial statements — Audit+ brings the entire preparation workflow into one platform.
              </p>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-indigo-800">From financial statements to XBRL</h4>
                <ol className="list-decimal list-inside space-y-2 text-indigo-800">
                  <li><strong>Import &amp; prepare</strong> — import trial balances, manage statutory data, map accounts, and prepare lead schedules in Audit+.</li>
                  <li><strong>Generate financial statements</strong> — prepare complete audited or unaudited financial statements.</li>
                  <li><strong>Export to RapidTagger</strong> — RapidTagger turns the financial statement into an XBRL zip file.</li>
                </ol>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-gray-700">Get started with Audit+</h4>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>
                    <strong>Sign up now</strong> — register your audit firm through the Audit+ partner page. The Audit+ team will review and activate your account.
                  </li>
                  <li>
                    <strong>Watch the demo</strong> — explore the features and see how Audit+ integrates with RapidTagger.
                  </li>
                </ul>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex flex-wrap justify-end items-center gap-3">
              <div className="flex items-center gap-2">
                <a
                  href={AUDITPLUS_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="f-btn f-btn-outline no-underline"
                >
                  Watch demo
                </a>
                <a
                  href={AUDITPLUS_SIGNUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="f-btn f-btn-primary no-underline"
                >
                  Explore Audit+
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
